import Icon from 'components/atoms/Icon';
import { ItemsCount } from 'components/ui/ItemsCount/ItemsCount';
import { projectsRoutes } from 'constants/navigation';
import {
  ProjectOrderField,
  ProjectStatus,
  ProjectTriboolFilter,
  SortOption,
  useProjectsFindManyPaginatedQuery,
} from 'generated/graphql';
import { useCallback, FC, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  HeaderSortDirection,
  Search,
  Table,
  TMultiselectOption,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { useSerializedQueryParams } from 'hooks/useQueryParams';
import NoSearchResultsTableContent from './components/NoSearchResultsTableContent/NoSearchResultsTableContent';
import {
  AtsLinkedFilter,
  emptyStageTypeFilterValues,
  StageTypeFilterValues,
} from './components/ProjectsFilterModal/ProjectsFilterModal.constants';
import {
  pruneHiringTeamUsers,
  toAtsLinkedFilterArg,
  toStageTypeFilterArgs,
  withStageTypeFilterDefaults,
} from './components/ProjectsFilterModal/ProjectsFilterModal.helpers';
import NoRolesMatchFiltersContent from './components/NoSearchResultsTableContent/NoRolesMatchFiltersContent';
import ProjectsFilterModal, {
  emptyProjectFilterFormValues,
} from './components/ProjectsFilterModal/ProjectsFilterModal';
import {
  DEFAULT_TABLE_PAGE_SIZE,
  getFiltersButtonAriaLabel,
  getFiltersCount,
  getProjectsErrorTitle,
  parseFromDateFilter,
  parseTableData,
  parseToDateFilter,
  tableHeaders,
} from './Projects.helpers';
import styles from './Projects.module.scss';

type Pagination = {
  page: number;
  pageSize: number;
};

type ProjectsUrlState = {
  search: string | null;
  orderBy: ProjectOrderField | null;
  orderDirection: SortOption | null;
  pageNumber: number;
  pageSize: number;
  isArchived: boolean | null;
  companies: TMultiselectOption[];
  hiringTeamUsers: TMultiselectOption[];
  atsLinked: AtsLinkedFilter | null;
  stageTypeFilters: StageTypeFilterValues;
  status: TMultiselectOption<ProjectStatus>[];
  dateCreatedFrom: string | null;
  dateCreatedTo: string | null;
  datePublishedFrom: string | null;
  datePublishedTo: string | null;
  dateLastEditedFrom: string | null;
  dateLastEditedTo: string | null;
  projectCandidatesFrom: string | null;
  projectCandidatesTo: string | null;
};

// Module scope, above the component — a stable object, and absent URL params
// fall back to these values inside the hook.
const initialUrlState: ProjectsUrlState = {
  search: null,
  orderBy: ProjectOrderField.ProjectCreatedAt,
  orderDirection: SortOption.Desc,
  pageNumber: 1,
  pageSize: DEFAULT_TABLE_PAGE_SIZE,
  isArchived: false,
  companies: [],
  hiringTeamUsers: [],
  atsLinked: AtsLinkedFilter.Any,
  // Must be a non-null object so the hook JSON-parses the URL value.
  stageTypeFilters: emptyStageTypeFilterValues(),
  status: [],
  dateCreatedFrom: null,
  dateCreatedTo: null,
  datePublishedFrom: null,
  datePublishedTo: null,
  dateLastEditedFrom: null,
  dateLastEditedTo: null,
  projectCandidatesFrom: null,
  projectCandidatesTo: null,
};

const Projects: FC = () => {
  const { handleMsgType } = useNotification();
  const navigate = useNavigate();

  const [urlParams, setUrlParams] =
    useSerializedQueryParams<ProjectsUrlState>(initialUrlState);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);

  // Same shape as before, now derived from the URL; feeds the query,
  // the filter count, the aria label, and the modal's initialValues.
  const filters: typeof emptyProjectFilterFormValues = useMemo(
    () => ({
      isArchived: urlParams.isArchived ?? false,
      companies: urlParams.companies,
      // A shared link can name users outside the companies it also names, so
      // they are dropped rather than silently filtering by someone invisible.
      hiringTeamUsers: pruneHiringTeamUsers(
        urlParams.hiringTeamUsers,
        urlParams.companies,
      ),
      atsLinked: urlParams.atsLinked ?? AtsLinkedFilter.Any,
      stageTypeFilters: withStageTypeFilterDefaults(
        urlParams.stageTypeFilters
      ),
      dateCreatedFrom: urlParams.dateCreatedFrom ?? '',
      dateCreatedTo: urlParams.dateCreatedTo ?? '',
      datePublishedFrom: urlParams.datePublishedFrom ?? '',
      datePublishedTo: urlParams.datePublishedTo ?? '',
      dateLastEditedFrom: urlParams.dateLastEditedFrom ?? '',
      dateLastEditedTo: urlParams.dateLastEditedTo ?? '',
      projectCandidatesFrom: urlParams.projectCandidatesFrom ?? '',
      projectCandidatesTo: urlParams.projectCandidatesTo ?? '',
      status: urlParams.status,
    }),
    [urlParams],
  );

  const { data, loading } = useProjectsFindManyPaginatedQuery({
    // TODO: Remove after implementing proper project caching for
    // project create, project delete and project edit.
    fetchPolicy: 'cache-and-network',
    variables: {
      filters: {
        textSearch: urlParams.search ?? '',
        withDeleted: filters.isArchived
          ? ProjectTriboolFilter.All
          : ProjectTriboolFilter.OnlyNot,
        companyIds: filters.companies.map((company) => Number(company.value)),
        hiringTeamUserIds: filters.hiringTeamUsers.map((hiringTeamUser) =>
          Number(hiringTeamUser.value),
        ),
        atsLinked: toAtsLinkedFilterArg(filters.atsLinked),
        stageTypeFilters: toStageTypeFilterArgs(filters.stageTypeFilters),
        dateCreated: {
          from: parseFromDateFilter(filters.dateCreatedFrom),
          to: parseToDateFilter(filters.dateCreatedTo),
        },
        dateLastEdited: {
          from: parseFromDateFilter(filters.dateLastEditedFrom),
          to: parseToDateFilter(filters.dateLastEditedTo),
        },
        datePublished: {
          from: parseFromDateFilter(filters.datePublishedFrom),
          to: parseToDateFilter(filters.datePublishedTo),
        },
        toProjectCandidatesCount: filters.projectCandidatesTo
          ? Number(filters.projectCandidatesTo)
          : undefined,
        fromProjectCandidatesCount: filters.projectCandidatesFrom
          ? Number(filters.projectCandidatesFrom)
          : undefined,
        statuses: filters.status.map((status) => status.value as ProjectStatus),
      },
      order: {
        field: urlParams.orderBy ?? ProjectOrderField.ProjectCreatedAt,
        direction: urlParams.orderDirection ?? SortOption.Desc,
      },
      pagination: {
        page: urlParams.pageNumber,
        size: urlParams.pageSize,
      },
    },
    onError: (error) => {
      handleMsgType({
        type: TNotification.error,
        title: getProjectsErrorTitle(error),
        message: error.message || 'Something went wrong. Please try again.',
        dismissable: true,
      });
    },
  });

  const tableRows = useMemo(
    () => data?.Projects.data?.map(parseTableData),
    [data?.Projects.data],
  );

  const handleSearch = (value: string) => {
    // Reset pagination on search change.
    setUrlParams({ search: value || null, pageNumber: 1 });
  };

  const handleSearchClearBtnClick = () => {
    handleSearch('');
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleRowClick = (rowValue: any) => {
    const projectUrl = projectsRoutes.editProjectView.url(rowValue.id);
    navigate(projectUrl);
  };

  const handleRowAltClick = (rowValue: { id: number }) => {
    const projectUrl = projectsRoutes.editProjectView.url(rowValue.id);
    window.open(projectUrl, '_blank');
  };

  const handlePaginationChange = ({
    page: newPage,
    pageSize: newPageSize,
  }: Pagination) => {
    setUrlParams({ pageNumber: newPage, pageSize: newPageSize });
  };

  const handleSubmitFilters = (
    formValues: typeof emptyProjectFilterFormValues,
  ) => {
    // Empty values become null so they are removed from the URL.
    // Reset pagination on filter change (company-client parity).
    setUrlParams({
      isArchived: formValues.isArchived || null,
      companies: formValues.companies,
      hiringTeamUsers: formValues.hiringTeamUsers,
      atsLinked:
        formValues.atsLinked === AtsLinkedFilter.Any
          ? null
          : formValues.atsLinked,
      stageTypeFilters: formValues.stageTypeFilters,
      status: formValues.status,
      dateCreatedFrom: formValues.dateCreatedFrom || null,
      dateCreatedTo: formValues.dateCreatedTo || null,
      datePublishedFrom: formValues.datePublishedFrom || null,
      datePublishedTo: formValues.datePublishedTo || null,
      dateLastEditedFrom: formValues.dateLastEditedFrom || null,
      dateLastEditedTo: formValues.dateLastEditedTo || null,
      projectCandidatesFrom: formValues.projectCandidatesFrom || null,
      projectCandidatesTo: formValues.projectCandidatesTo || null,
      pageNumber: 1,
    });
  };

  const handleSortChange = useCallback(
    (field: string, tableSortDirection: HeaderSortDirection) => {
      if (tableSortDirection === HeaderSortDirection.NONE) {
        // Clearing the sort removes the params → falls back to the default
        // (created-at desc), same as a fresh page load.
        setUrlParams({ orderBy: null, orderDirection: null });
        return;
      }

      setUrlParams({
        orderBy: field as ProjectOrderField,
        orderDirection:
          tableSortDirection === HeaderSortDirection.ASC
            ? SortOption.Asc
            : SortOption.Desc,
      });
    },
    [setUrlParams],
  );

  const handleTableSort = useMemo(() => {
    let sortDirection = HeaderSortDirection.NONE;

    if (urlParams.orderDirection === SortOption.Asc) {
      sortDirection = HeaderSortDirection.ASC;
    }

    if (urlParams.orderDirection === SortOption.Desc) {
      sortDirection = HeaderSortDirection.DESC;
    }

    return {
      sortDirection,
      onClick: handleSortChange,
      sortedHeader: urlParams.orderBy || '',
    };
  }, [urlParams.orderDirection, urlParams.orderBy, handleSortChange]);

  const filterCount = useMemo(() => {
    return getFiltersCount(filters) || undefined;
  }, [filters]);
  const filtersButtonAriaLabel = getFiltersButtonAriaLabel(filters);

  const handleResetFilters = () =>
    handleSubmitFilters(emptyProjectFilterFormValues);

  const itemsTotal = data?.Projects.pageInfo?.itemsTotal;
  const createProjectUrl = projectsRoutes.createProject.url();

  return (
    <div className={styles.container}>
      <h1 className={styles.heading}>Roles</h1>
      <Table
        className={styles.table}
        headerData={tableHeaders}
        rowData={tableRows}
        linkComponent={Link}
        onRowClick={handleRowClick}
        onRowCtrlOrCmdClick={handleRowAltClick}
        loading={loading}
        hasPagination
        unsortableHeaderKeys={['archived']}
        tableSearchDirty={Boolean(urlParams.search) || Boolean(filterCount)}
        externalSort={handleTableSort}
        noSearchResultsText={
          filterCount ? (
            <NoRolesMatchFiltersContent onResetFilters={handleResetFilters} />
          ) : (
            <NoSearchResultsTableContent />
          )
        }
        pagination={{
          currentPageSize: urlParams.pageSize,
          onChange: handlePaginationChange,
          totalNumberOfItems: itemsTotal || 0,
          page: urlParams.pageNumber,
        }}
        hasToolbar
        toolbarConf={{
          componentListLeftStart: (
            <ItemsCount
              count={itemsTotal || 0}
              ariaLabelSuffix="projects in the table"
            />
          ),
          componentListLeft: (
            <div className={styles.toolbarContainer}>
              <form
                onSubmit={(e) => e.preventDefault()}
                className={styles.searchForm}
              >
                <Search
                  internalSearch={false}
                  onSearch={handleSearch}
                  inputValue={urlParams.search ?? ''}
                  onClearBtnClick={handleSearchClearBtnClick}
                  ariaLabel="Search"
                  ariaDisabled={false}
                />
              </form>
              <Button
                variant="secondary"
                leftIcon={<Icon icon="filter" />}
                onClick={() => setIsFiltersModalOpen(true)}
                counter={filterCount}
                aria-label={filtersButtonAriaLabel}
                data-testid="projects-page-filter-button"
              >
                Filter
              </Button>
            </div>
          ),
          componentListRight: (
            <Link to={createProjectUrl}>
              <Button leftIcon={<Icon icon="add" />} variant="secondary">
                Create role
              </Button>
            </Link>
          ),
        }}
      />
      <ProjectsFilterModal
        isOpen={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        onSubmit={handleSubmitFilters}
        initialValues={filters}
      />
    </div>
  );
};

export default Projects;
